import { FC, useRef, useState, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import ModalContainer from "react-modal-promise";
import _ from "lodash";

// @ts-ignore
import PopoverCustom from "components/PriorityApp/SimpleView/PopoverCustom/index";

// @ts-ignore
import UnitsSelector from "components/Journeys/Measurement/UnitsSelector";

// @ts-ignore
import CustomSelect, { getOptionWithLabel } from "components/Journeys/Mission/CustomSelect";

// @ts-ignore
import { ListAreas, CreateArea, DeleteArea, CreateCategory, DeleteCategory } from "Redux/ListAreas/action";

// @ts-ignore
import { UpdateGoal } from "Redux/UpdateGoal/action";

// @ts-ignore
import { getCookie } from "utils/StorageVariables";

// @ts-ignore
import useDeviceType from "../../../../Hooks/ResponsiveHook";

// @ts-ignore
import { confirmModal } from "components/Journeys/Mission/ConfirmModal";

// @ts-ignore
import { track } from 'utils/amplitude';

// @ts-ignore
import * as Styles from "./styled.js";

interface IHeader {
  data: any;
  readOnly: boolean;
  isPrivate?: boolean;
  onUpdate: (fields: { [key: string]: any }, areaId: string, goalId: string, callback?: () => void) => void;
  onAreaNameUpdate: (areaName: string, id: string, i: number, isCustom: boolean, color?: string, callback?: () => void) => void;
  onAddMetricOpen: () => void;
  onAddMetricToggle: () => void;
}

const Header: FC<IHeader> = ({
  data,
  readOnly,
  isPrivate,
  onUpdate,
  onAreaNameUpdate,
  onAddMetricOpen,
  onAddMetricToggle,
}) => {
  const $inputRef = useRef<HTMLDivElement>(null);

  const [isAssistantOpen, setIsAssistantOpen] = useState(false);
  const [openedDropdown, setOpenedDropdown] = useState<{ type: string | null, id: string | null }>({
    type: null,
    id: null,
  });
  
  // Local state for optimistic UI updates
  const [localData, setLocalData] = useState<any>(data);

  const screenSizes = useDeviceType();
  
  // Update local state when props change
  useEffect(() => {
    setLocalData(data);
  }, [data]);
  
  const dispatch = useDispatch();

  // @ts-ignore
  const { data: predefinedMetrics } = useSelector((state) => state.predefinedMetricsReducer);

  // @ts-ignore
  const { data: customMetrics } = useSelector((state) => state.customMetricsReducer);
  
  // @ts-ignore
  const { data: predefinedAreas, customData: customAreas } = useSelector((state) => state.listAreaReducer);

  const isMobile = screenSizes === "mobile";
  
  // Fetch areas and categories on component mount
  useEffect(() => {
    const subdomainId = getCookie("subdomainId");

    dispatch(ListAreas(subdomainId));
    dispatch(ListAreas(subdomainId, true));
  }, [dispatch]);

  const onUnitsChangeHandle = (id: string) => (e: any) => {
    if (e.key === "custom")
      onAddMetricOpen();
    else if (e.key === "manage")
      onAddMetricToggle();
  };

  const handleMeasurementsChange = (id: string) => (field: string, value: string) => {
    onUpdate({ [field]: value }, id, data?.goal?.id);
  };

  const handleMeasurementsBulkChange = (id: string) => (values: any, close: boolean) => {
    onUpdate(values, id, data?.goal?.id, () => {
      if (close) {
        $inputRef.current?.click();
      }
    });
  }

  const handleAssistantOpen = (value: boolean) => {
    setIsAssistantOpen(value);
  }
  const handleUnitsClose = () => {
    setIsAssistantOpen(false);
    $inputRef.current?.click();
  }

  const measurementValue = `${
    data?.goal?.measurement_current_value ||
    data?.goal?.measurement_target_value
      ? `${data?.goal?.measurement_current_value ?? " - "}/${
          data?.goal?.measurement_target_value ?? " - "
        }`
      : "-"
  } ${readOnly ? "" : data?.goal?.measurement_metric_name}`;

  // Helper function to get the selected area
  const getSelectedArea = () => {
    return [...predefinedAreas || [], ...customAreas || []].find(
      (area: any) => area.name === localData?.name
    );
  };

  // Debounced function for updating category data
  const handleCategoryUpdate = useCallback(
    _.debounce(
      (updatedData: any, areaId: string, callback?: () => void) => {
        onUpdate(updatedData, areaId, data?.goal?.id, callback);
      },
      500
    ),
    [onUpdate, data?.goal?.id]
  );

  // Handle area selection
  const handleSelectArea = async (area: any, areaId: string) => {
    // Check if mission statement is available
    if (data?.goal?.has_mission_statement) {
      // @ts-ignore
      const modal = await confirmModal({ id: data?.goal?.id });
      if (!modal) return;
    }

    let foundArea = predefinedAreas?.find((item: any) => item?.id === area?.value);
    
    if (!foundArea) {
      if (!area.label) return;
      
      // Create a copy of the local data
      const updatedLocalData = { ...localData };
      
      // Update local data immediately
      updatedLocalData.name = area.label;
      updatedLocalData.is_custom = true;
      updatedLocalData.goal = {
        ...updatedLocalData.goal,
        category_name: "" // Reset category on area change
      };
      
      // Update local state for immediate UI feedback
      setLocalData(updatedLocalData);
      
      // Use the new onAreaNameUpdate function for area name updates
      onAreaNameUpdate(area.label, areaId, data.i, true);
      
      // Update goal to reset category
      handleCategoryUpdate({
        category_name: ""
      }, areaId);
    } else {
      // Create a copy of the local data
      const updatedLocalData = { ...localData };
      
      // Update local data immediately
      updatedLocalData.name = foundArea?.name;
      updatedLocalData.color = foundArea?.color;
      updatedLocalData.is_custom = false;
      updatedLocalData.goal = {
        ...updatedLocalData.goal,
        category_name: "" // Reset category on area change
      };
      
      // Update local state for immediate UI feedback
      setLocalData(updatedLocalData);
      
      // Use the new onAreaNameUpdate function for area name updates
      onAreaNameUpdate(foundArea?.name, areaId, data.i, false, foundArea?.color);
      
      // Update goal to reset category
      handleCategoryUpdate({
        category_name: ""
      }, areaId);
    }

    // Amplitude tracking
    track("Goal Area Selected", {
      goal_area: area?.label || foundArea?.name,
      goal_id: data?.goal?.id
    });

    // Reset category in the goal
    dispatch(UpdateGoal(data?.goal?.id, { category_name: "" }, () => {}, false));
  };

  // Handle category selection
  const handleSelectCategory = async (category: any, areaId: string, isCustom: boolean) => {
    // Check if mission statement is available
    if (data?.goal?.has_mission_statement) {
      if (isCustom) return;

      // @ts-ignore
      const modal = await confirmModal({ id: data?.goal?.id });
      if (!modal) return;
    }

    if (!category?.label) return;

    // Create a copy of the local data
    const updatedLocalData = { ...localData };
    
    // Update local data immediately
    updatedLocalData.goal = {
      ...updatedLocalData.goal,
      category_name: category?.label,
      is_custom: isCustom
    };
    
    // Update local state for immediate UI feedback
    setLocalData(updatedLocalData);

    // Update category with debounced function
    handleCategoryUpdate({
      // goal: {
        category_name: category?.label,
        is_custom: isCustom
      // }
    }, areaId);

    // Amplitude tracking
    track("Goal Category Selected", {
      goal_category: category?.label,
      goal_id: data?.goal?.id
    });
  };

  // Handle area creation
  const handleCreateArea = (value: string, callback: (success: any) => void) => {
    const existingArea = [...predefinedAreas || [], ...customAreas || []].find(
      (item: any) => item.name === value
    );

    if (existingArea) {
      callback(null);
      return;
    }

    const formData = new FormData();
    const subdomainId = getCookie("subdomainId");
    formData.append('name', value);
    formData.append('subdomain', subdomainId);

    dispatch(CreateArea(formData, callback));
  };

  // Handle category creation
  const handleCreateCategory = (areaData: any) => (value: string, callback: (success: any) => void) => {
    if (!areaData) {
      callback(null);
      return;
    }

    const existingCategory = areaData.categories?.find(
      (cat: any) => cat.name === value
    );

    if (existingCategory) {
      callback(null);
      return;
    }

    const formData = new FormData();
    formData.append('name', value);
    formData.append('area', areaData.id);

    dispatch(CreateCategory(formData, callback));
  };

  // Handle dropdown visibility changes
  const handleDropdownVisibleChange = (type: string, id: string) => (visible: boolean) => {
    if (visible) {
      setOpenedDropdown({ type, id });
    } else if (openedDropdown.type === type && openedDropdown.id === id) {
      setOpenedDropdown({ type: null, id: null });
    }
  };

  return (
    <Styles.Wrapper $color={localData?.color}>
      <Styles.Row>
        <Styles.PriorityCol>
          <Styles.Title>Priority</Styles.Title>
          <Styles.Circle>
            {typeof localData?.i === "number" && !Number.isNaN(localData.i) ? localData.i + 1 : 0}
          </Styles.Circle>
        </Styles.PriorityCol>
        <Styles.AreaCol>
          <Styles.Title>Area</Styles.Title>
          {readOnly || isPrivate ? null : (
            <CustomSelect
              type="area"
              text="Area"
              // @ts-ignore
              placement="bottomLeft"
              dropdownPosition="header"
              disabled={isPrivate}
              options={[
                {
                  label: "Areas",
                  options: predefinedAreas?.map((area: any) => ({
                    label: area?.name,
                    value: area?.id,
                  })) || [],
                },
                {
                  label: "Custom Areas",
                  options: customAreas?.map((area: any) => ({
                    label: area?.name,
                    value: area?.id,
                    custom: true
                  })) || []
                }
              ]}
              error=""
              value={getOptionWithLabel(localData?.name, predefinedAreas, customAreas) || undefined}
              onSelect={(area) => handleSelectArea(area, data?.id)}
              onCreate={handleCreateArea}
              onDelete={(value) => dispatch(DeleteArea(value))}
              onDropdownVisibleChange={handleDropdownVisibleChange('area', data?.id)}
            />
          )}
          {readOnly ? (
            <Styles.Heading title={localData?.name}>{localData?.name}</Styles.Heading>
          ) : null}
          {isPrivate ? <Styles.Heading $private>Private</Styles.Heading> : null}
        </Styles.AreaCol>
        <Styles.CategoryCol>
          <Styles.Title>Category</Styles.Title>
          {readOnly || isPrivate ? null : (
            <CustomSelect
              type="category"
              text="Category"
              dropdownPosition="header"
              disabled={isPrivate}
              options={(() => {
                // Find the selected area by name since we might not have area_id
                const selectedArea = [...predefinedAreas || [], ...customAreas || []].find(
                  (area: any) => area.name === localData?.name
                );
                
                if (!selectedArea) return [];
                
                const predefinedCategories = selectedArea.categories?.filter((cat: any) => !cat.custom) || [];
                const customCategories = selectedArea.categories?.filter((cat: any) => cat.custom) || [];
                
                return [
                  ...(predefinedCategories.length ? [{
                    label: "Categories",
                    options: predefinedCategories.map((cat: any) => ({
                      label: cat.name,
                      value: cat.id
                    }))
                  }] : []),
                  ...(customCategories.length ? [{
                    label: "Custom Categories",
                    options: customCategories.map((cat: any) => ({
                      label: cat.name,
                      value: cat.id,
                      custom: true
                    }))
                  }] : [])
                ];
              })()}
              error=""
              value={getOptionWithLabel(localData?.goal?.category_name, [...predefinedAreas, ...customAreas]?.find((area) => area?.name === localData?.name)?.categories || [], []) || undefined}
              onSelect={(category) =>
                handleSelectCategory(
                  category,
                  data?.id,
                  Boolean((category as any)?.custom)
                )
              }
              onCreate={handleCreateCategory(getSelectedArea())}
              onDelete={(value) => {
                const selectedArea = getSelectedArea();
                if (selectedArea) {
                  dispatch(DeleteCategory(value, selectedArea.id));
                }
              }}
              onDropdownVisibleChange={handleDropdownVisibleChange('category', data?.id)}
            />
          )}
          {readOnly ? (
            <Styles.Heading title={localData?.goal?.category_name}>
              {localData?.goal?.category_name}
            </Styles.Heading>
          ) : null}
          {isPrivate ? <Styles.Heading $private>Private</Styles.Heading> : null}
        </Styles.CategoryCol>
        <Styles.MeasurementCol>
          <Styles.Title>Measurement</Styles.Title>
          {readOnly || isPrivate ? null : (
            <PopoverCustom
              keepOpen={isAssistantOpen}
              defaultBoundaryElement
              positions={["bottom", "top"]}
              align={isMobile ? "start" : "center"}
              width={isMobile ? "320px" : "448px"}
              reposition={false}
              popoverStyle={{
                containerStyle: {
                  zIndex: "998",
                },
              }}
              content={
                <UnitsSelector
                  id={data?.id}
                  predefinedData={predefinedMetrics}
                  customData={customMetrics}
                  goalData={data?.goal}
                  onClick={onUnitsChangeHandle(data?.id)}
                  onChange={handleMeasurementsChange(data?.id)}
                  onBulkChange={handleMeasurementsBulkChange(data?.id)}
                  onOpen={handleAssistantOpen}
                  onClose={handleUnitsClose}
                />
              }
            >
              <Styles.PopoverToggle>
                <Styles.InputAreaDiv
                  title={measurementValue}
                  resize="none"
                  ref={$inputRef}
                >
                  {measurementValue}
                </Styles.InputAreaDiv>
              </Styles.PopoverToggle>
            </PopoverCustom>
          )}
          {readOnly ? (
            <Styles.Heading $measurement>
              {measurementValue} <span>{localData?.goal?.measurement_metric_name}</span>
            </Styles.Heading>
          ) : null}
          {isPrivate ? <Styles.Heading $private>Private</Styles.Heading> : null}
        </Styles.MeasurementCol>
      </Styles.Row>

      <ModalContainer />
    </Styles.Wrapper>
  );
};

export default Header;
